import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ShiftData from '../../../assets/data/ShiftData';
import TimezoneData from '../../../assets/data/TimezoneData';
import UserData from '../../../assets/data/UserData';
import CustomAutocomplete from '../../../components/CustomAutocomplete';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, StyledCheckbox } from '../../../components/StyledComponents';
import QuestionIcon16pxSvg from './../../../assets/images/icons/QuestionIcon16pxSvg';
import CustomInput from './../../../components/CustomInput';
import CustomSelect from './../../../components/CustomSelect';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import DefaultTooltip from './../../../components/DefaultTooltip';
import Combobox from '../../../components/Combobox';
import { addEventLog, AddRequest } from '../../../axios/Eventlog/EventLogApi';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import SendIcon from '@mui/icons-material/Send';
import { DateTime } from "luxon";
import { useAuth } from '../../../context/AuthContext';
import { useLoading } from '../../../context/LoadingContext';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getShiftByUser } from '../../../axios/Shift/ShiftApi';

dayjs.extend(utc);

const AddRequestDialog = ({ onClose, usersData, shiftsData, onRefresh, info, requestTypeId, selectedDate }) => {
   // const [isFormValid, setIsFormValid] = useState(false);
   const { t } = useTranslation();
   const [show, hide] = useLoading();
   const [isSubmitting, setIsSubmitting] = useState(false);
   const { genaralSettingDateFormat, genaralSettingTimeFormat, dateTimeFormat, genaralSettingTimeZone } = useAuth();
   const [shiftsData1, setShiftsData] = useState([]);

   const normalizeTime = (time) => {
      if (!time) return null;

      // HH:mm → HH:mm:00
      if (/^\d{2}:\d{2}$/.test(time)) {
         return `${time}:00`;
      }

      // HH:mm:ss
      if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
         return time;
      }

      return null;
   };



   const user = usersData.find(user => user.employeeCode === info?.employeeCode);


   const handleCloseDialog = () => {
      onClose();
   };

   const MAX_DESCRIPTION_LENGTH = 255;

   const handleInputChange = (e) => {
      const { name } = e.target;
      let { value } = e.target;

      // ✅ Giới hạn description nhưng KHÔNG return
      if (name === 'description') {
         value = value.slice(0, MAX_DESCRIPTION_LENGTH);
      }

      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const initialFormData = {
      eventType: 1,
      username: info.fullName,
      selectDate: selectedDate,
      selectTime: DateTime.local().toFormat('HH:mm:ss'),
      timeZone: DateTime.local().toFormat("ZZ"),
      description: '',
      userId: info.userId,
      requestTypeId: requestTypeId,
      shift: shiftsData1[0]?.shiftId,
      location: info?.location || '',
   };
   const [formData, setFormData] = useState(initialFormData);

   const handleDataChange = (name, newData) => {
      setFormData((prevData) => ({
         ...prevData,
         [name]: newData,
      }));
   };

   const [useDifferentShiftDate, setUseDifferentShiftDate] = useState(false);

   const handleCheckedChange = (event) => {
      setUseDifferentShiftDate(event.target.checked);
   };

   useEffect(() => {
      if (!formData.userId || !formData.selectDate || !formData.selectTime) return;

      const fetchShift = async () => {
         try {
            show();

            const dateTimeUTC = buildAttendanceDateTimeUTC(
               formData.selectDate,
               formData.selectTime
            );

            const payload = {
               userId: formData.userId,        // ✅ user đang chọn
               dateTime: dateTimeUTC,          // ✅ ISO8601 UTC (+0)
               langId: localStorage.getItem("i18nextLngId"),
            };

            const response = await getShiftByUser(payload);
            setShiftsData(response.data.data || []);
         } catch (error) {
            toast.error(t(error?.response?.data));
         } finally {
            hide();
         }
      };

      fetchShift();
   }, [
      formData.userId,
      formData.selectDate,
      formData.selectTime
   ]);

   const timezoneData = TimezoneData();

   const browserTZ = dayjs.tz.guess();

   const buildAttendanceDateTimeUTC = (date, time) => {
      if (!date || !time || !genaralSettingTimeZone) return null;

      // yyyy-MM-dd theo ngày chọn
      const yyyyMMdd = format(date, "yyyy-MM-dd");

      // Tạo datetime theo timezone công ty
      const companyDateTime = dayjs.tz(
         `${yyyyMMdd} ${time}`,
         "YYYY-MM-DD HH:mm:ss",
         browserTZ
      );

      // Convert về UTC ISO string
      return companyDateTime.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
   };

   const handleSubmitAddRequest = async () => {

      const attendanceDateTime = buildAttendanceDateTimeUTC(
         formData.selectDate,
         formData.selectTime,
      );

      const formatted = format(new Date(formData.selectDate), 'yyyy-MM-dd')
      const payload = {
         ...formData,
         attendanceDateTime,
         selectDate: formatted,
         timeZone: browserTZ,
         langId: localStorage.getItem("i18nextLngId")
      };

      setIsSubmitting(true);
      try {
         const response = await AddRequest(payload);
         if (response?.data?.responseCode === 0) {
            handleCloseDialog();
            toast.success(t('common.add-request-successfully'));
            onRefresh();
         } else {
            toast.error(response?.data?.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response.data.message));
            }
         } else {
            toast.error(t('common.network-timeout'));
         }
      } finally {
         setIsSubmitting(false);
      }
   };


   const sortedShifts = [...shiftsData1].sort((a, b) => b.shiftId - a.shiftId);

   useEffect(() => {
      if (sortedShifts.length > 0) {
         setFormData(prev => ({
            ...prev,
            shift: sortedShifts[0]?.shiftId    // <-- chọn "Test" mặc định
         }));
      }
   }, [shiftsData1]);

   return (
      <CustomDialog open transitionDuration={300} maxWidth={'xl'} loading={isSubmitting} onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('common.request-new-event')}</p>
               </div>
               <GrayIconButton onClick={handleCloseDialog} disabled={isSubmitting}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            {/* <p className='pt-6 text-sm text-[#DC2626] pb-2'>{t('common.required-fields-are-marked-in-red')} "*".</p> */}

            <div className='pt-3 space-y-3'>
               <div className='space-y-3'>
                  <p>{t('common.event-type')}</p>
                  <FormControl
                     component='fieldset'
                     sx={{
                        '.MuiFormGroup-root.MuiFormGroup-row': {
                           gap: '20px',
                        },
                     }}
                  >
                     <RadioGroup row aria-label='Event type' name='eventType' value={formData.eventType} onChange={handleInputChange}>
                        <FormControlLabel value={1} control={<Radio size='small' />} label={t('attendance.CHECK_IN')} />
                        <FormControlLabel value={2} control={<Radio size='small' />} label={t('attendance.CHECK_OUT')} />
                        {/* <FormControlLabel value={3} control={<Radio size='small' />} label='Pending check-in' />
                        <FormControlLabel value={4} control={<Radio size='small' />} label=' Pending check-out' /> */}
                        {/* <FormControlLabel value='access' control={<Radio size='small' />} label='Access' /> */}
                     </RadioGroup>
                  </FormControl>
               </div>

               <div className='grid grid-cols-12 md:gap-x-3 gap-y-3'>
                  <div className='col-span-12 md:col-span-6 space-y-3'>
                     <label>
                        {t('common.userName')}
                     </label>
                     <CustomInput
                        name='username'
                        inputType='text'
                        readOnly={true}
                        value={formData.username}
                        handleInputChange={handleInputChange}
                        labelEnable={false}
                        placeholder={t('common.userName')}
                     />
                  </div>
                  <div className='col-span-12 md:col-span-6 space-y-3'>
                     <label>{t('common.time-attendance')}<span className='text-[#EF4444] ml-1'>*</span></label>
                     <CustomDateTimePicker
                        mode="singleTime"
                        value={
                           formData.selectDate && formData.selectTime
                              ? dayjs(
                                 `${dayjs(formData.selectDate).format('YYYY-MM-DD')} ${normalizeTime(formData.selectTime) || formData.selectTime}`
                              ).toDate()
                              : formData.selectDate || null
                        }
                        dateTimeFormat={dateTimeFormat}
                        onChange={(newDateTime) => {
                           const d = dayjs(newDateTime);
                           setFormData((prev) => ({
                              ...prev,
                              selectDate: d.toDate(),
                              selectTime: d.format('HH:mm:ss'),
                           }));
                        }}
                     />
                  </div>

                  <div className='col-span-12 md:col-span-12 space-y-3'>
                     <label>{t('common.shift')}<span className='text-[#EF4444] ml-1'>*</span></label>
                     <CustomSelect
                        name='shift'
                        value={formData.shift}
                        onChange={handleInputChange}
                        options={sortedShifts}
                        dataType='shift'
                     />
                  </div>

                  {/* <div className='col-span-12 md:col-span-6 space-y-3'>
                     <label className='text-[#171717]'>{t('common.location')}</label>
                     <CustomInput
                        inputType='text'
                        readOnly={true}
                        value={formData.location}
                        labelEnable={false}
                     />
                  </div> */}

                  <div className='col-span-12 space-y-3'>
                     <label>{t('common.reason')}<span className='text-[#EF4444] ml-1'>*</span></label>
                     <CustomInput
                        name='description'
                        inputType='text'
                        value={formData.description}
                        handleInputChange={handleInputChange}
                        labelEnable={false}
                        placeholder={t('common.reason')}
                     />

                     <div
                        className={`flex justify-end text-xs ${formData.description.length >= MAX_DESCRIPTION_LENGTH
                           ? 'text-red-500'
                           : 'text-gray-500'
                           }`}
                     >
                        {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
                     </div>
                  </div>
               </div>
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isSubmitting}>
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton onClick={(e) => {
                  handleSubmitAddRequest()
               }}
                  disabled={isSubmitting || !formData.description.trim()}
                  // disabled={!isFormValid}
                  sx={{
                     borderRadius: '10px',
                     textDecoration: 'none'
                  }}>
                  <p>{t('button.send-request')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default AddRequestDialog;
